<template>
    <v-container fluid>
      <v-card>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="selectedWeek"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedWeek"
                    label="Select Week"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="selectedWeek"
                  type="date"
                  no-title
                  scrollable
                  @input="selectWeek"
                  :min="minSelectableDate"
                  :max="maxSelectableDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="mealType"
                :items="mealTypes"
                label="Meal Time"
                outlined
                @change="onMealTypeChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-alert type="info" outlined>
                Available Points: {{ availablePoints }}
              </v-alert>
            </v-col>
          </v-row>
  
          <v-data-table
            :headers="headers"
            :items="mealPlan"
            :items-per-page="7"
            class="elevation-1"
          >
            <template v-slot:item.date="{ item }">
              {{ formatDate(item.date) }}
            </template>
            <template v-slot:item.mealCombo="{ item }">
              <v-autocomplete
                v-model="item.mealCombo"
                :items="filteredMealCombos"
                item-text="combo_name"
                item-value="id"
                label="Meal Combo"
                @change="updateMealDetails(item)"
                :disabled="!canEditOrSave(item.date)"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.combo_name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Credits: {{ item.combo_points }} | Type: {{ item.combo_type }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </template>
            <template v-slot:item.comboItems="{ item }">
              <v-chip-group column>
                <v-chip
                  v-for="(comboItem, index) in item.comboItems"
                  :key="index"
                  small
                  :color="getChipColor(comboItem)"
                >
                  {{ comboItem }}
                </v-chip>
              </v-chip-group>
            </template>
            <template v-slot:item.points="{ item }">
              {{ item.points }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-rating
                v-if="isPastMeal(item.date)"
                v-model="item.rating"
                color="amber"
                dense
                half-increments
                hover
                size="18"
              ></v-rating>
              <v-btn
                v-else-if="canEditOrSave(item.date)"
                small
                color="primary"
                @click="saveMealSelection(item)"
                :disabled="!item.mealCombo || !canEditOrSave(item.date)"
              >
                {{ item.saved ? 'Edit' : 'Save' }}
              </v-btn>
              <v-btn
                v-else
                small
                color="error"
                @click="cancelMeal(item)"
                :disabled="!canCancelMeal(item.date)"
              >
                Cancel
              </v-btn>
              
            </template>
            <!-- <template v-slot:item.rating="{ item }">
              
            </template> -->
          </v-data-table>
        </v-card-text>
      </v-card>
  
      <v-dialog v-model="customizationDialog" max-width="600px">
        <v-card>
          <v-toolbar flat color="primary">
            <v-card-title>Customize Your Meal</v-card-title>
            <v-spacer></v-spacer>
            <v-btn icon color="red" @click="closeCustomizationDialog"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="mt-2">
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.flatbread"
                  :items="getFlatbreadOptions()"
                  item-text="item_name"
                  item-value="id"
                  label="Flatbread"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="customMeal.flatbreadQuantity"
                  type="number"
                  label="Quantity"
                  outlined
                  :disabled="!customMeal.flatbread"
                  min="0"
                  @input="calculateCustomMealPoints"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.rice"
                  :items="getRiceOptions()"
                  item-text="item_name"
                  item-value="id"
                  label="Rice"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="customMeal.riceQuantity"
                  type="number"
                  label="Quantity (g)"
                  outlined
                  :disabled="!customMeal.rice"
                  min="0"
                  step="50"
                  @input="calculateCustomMealPoints"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.curry"
                  :items="getCurryOptions()"
                  item-text="item_name"
                  item-value="id"
                  label="Curry"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.curryPortion"
                  :items="portionOptions"
                  label="Portion"
                  outlined
                  :disabled="!customMeal.curry"
                  @change="calculateCustomMealPoints"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.dry"
                  :items="getDryOptions()"
                  item-text="item_name"
                  item-value="id"
                  label="Dry"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="customMeal.dryPortion"
                  :items="portionOptions"
                  label="Portion"
                  outlined
                  :disabled="!customMeal.dry"
                  @change="calculateCustomMealPoints"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-checkbox v-model="customMeal.dalCurry" label="Dal Curry" @change="calculateCustomMealPoints"></v-checkbox>
              </v-col>
              <v-col cols="3">
                <v-checkbox v-model="customMeal.salad" label="Salad" @change="calculateCustomMealPoints"></v-checkbox>
              </v-col>
              <v-col cols="3">
                <v-checkbox v-model="customMeal.curd" label="Curd" @change="calculateCustomMealPoints"></v-checkbox>
              </v-col>
              <v-col cols="3">
                <v-checkbox v-model="customMeal.dessert" label="Dessert" @change="calculateCustomMealPoints"></v-checkbox>
              </v-col>
            </v-row>
            <v-btn color="primary" @click="addExtraItem">
              <v-icon left>mdi-plus</v-icon> Add Extra Item
            </v-btn>
            <v-row v-for="(item, index) in customMeal.extraItems" :key="index">
              <v-col cols="6">
                <v-text-field v-model="item.name" label="Item Name" outlined></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model.number="item.points" type="number" label="Points" outlined></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-btn icon @click="removeExtraItem(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip color="success" outlined class="font-weight-bold">Credits: {{customMeal.points}} points</v-chip>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text outlined @click="closeCustomizationDialog">Cancel</v-btn>
            <v-btn color="blue darken-1" text outlined @click="saveCustomization">Add Combo</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
        {{ snackbarText }}
      </v-snackbar>
      <!-- {{ mealCombos }} -->
    </v-container>
  </template>
  
  <script>
import secure_axios from "../services/secure_axios";

  
  export default {
    data: () => ({
      menu: false,
      selectedWeek: new Date().toISOString().substr(0, 10),
      mealType: 'Lunch',
      mealTypes: ['Breakfast', 'Lunch', 'Dinner'],
      headers: [
        { text: 'Date', value: 'date' ,width: '15%'},
        { text: 'Meal Combo', value: 'mealCombo',width: '20%' },
        { text: 'Combo Items', value: 'comboItems',width: '50%' },
        { text: 'Credits Points', value: 'points',width: '5%' },
        { text: 'Actions', value: 'actions', width: '10%',sortable: false },
        // { text: 'Rating', value: 'rating' }
      ],
      mealPlan: [],
      mealCombos: [],
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      customizationDialog: true,
      customMeal: {
        flatbread: '',
        flatbreadQuantity: 0,
        rice: '',
        riceQuantity: 0,
        curry: '',
        curryPortion: '',
        dry: '',
        dryPortion: '',
        dalCurry: false,
        salad: false,
        curd: false,
        dessert: false,
        points: 0,
        extraItems: []
      },
      portionOptions: ['Small', 'Regular', 'Large'],
      availablePoints: 70, // 10 points per day for a week
      currentCustomizationIndex: null
    }),
  
    computed: {
      perferedAPI() {
        return this.$store.getters.perferedAPI;
      }, 
      formattedWeek() {
        return this.getWeekRange(this.selectedWeek);
      },
      minSelectableDate() {
        const today = new Date();
        return today.toISOString().substr(0, 10);
      },
      maxSelectableDate() {
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 14); // Allow selection up to 2 weeks in the future
        return maxDate.toISOString().substr(0, 10);
      },
      filteredMealCombos() {
        return this.mealCombos.filter(combo => 
          combo.combo_availability_time.includes(this.mealType.toLowerCase())
        );
      }
    },
  
    watch: {
      selectedWeek() {
        this.generateMealPlan();
      }
    },
  
    mounted() {
      this.generateMealPlan();
      this.fetchMealCombos();
    },
  
    methods: {
      async fetchMealCombos() {
        try {          
          const response = await secure_axios.get(`${this.perferedAPI}api/meal-combos`);
          this.mealCombos = response.data.map(combo => ({
            ...combo,
            combo_availability_time: combo.combo_availability_time.map(time => time.toLowerCase())
          }));
          this.mealCombos.push({"combo_availability_time": [ "breakfast", "lunch", "dinner" ], combo_name: 'Custom Combo',combo_points:'',combo_type:'custom', mealCombo: 'custom' })
        } catch (error) {
          console.error('Error fetching meal combos:', error);
          this.showSnackbar('Error fetching meal combos', 'error');
        }
      },
      async fetchCustomCombos() {
        try {          
          const response = await secure_axios.get(`${this.perferedAPI}api/custom-combos`);
          this.customCombos = response.data.map(combo => ({
            ...combo,
            combo_availability_time: combo.combo_availability_time.map(time => time.toLowerCase())
          }));
        } catch (error) {
          console.error('Error fetching meal combos:', error);
          this.showSnackbar('Error fetching meal combos', 'error');
        }
      },
  
      generateMealPlan() {
        const startDate = new Date(this.selectedWeek);
        this.mealPlan = Array.from({ length: 7 }, (_, i) => {
          const currentDate = new Date(startDate);
          currentDate.setDate(startDate.getDate() + i);
          return {
            date: currentDate,
            mealCombo: '',
            comboItems: [],
            points: 0,
            customized: false,
            saved: false,
            rating: 0
          };
        });
      },
  
      formatDate(date) {
        return new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      },
  
      selectWeek(date) {
        const selectedDate = new Date(date);
        const dayOfWeek = selectedDate.getDay();
        const diff = selectedDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const startOfWeek = new Date(selectedDate.setDate(diff));
        this.selectedWeek = startOfWeek.toISOString().substr(0, 10);
        this.$refs.menu.save(this.selectedWeek);
        this.generateMealPlan();
      },
  
      getWeekRange(date) {
        const startDate = new Date(date);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
      },
  
      updateMealDetails(item) {
      if (item.mealCombo === 'custom') {
        this.openCustomizationDialog(item);
      } else {
        const selectedCombo = this.mealCombos.find(combo => combo.id === item.mealCombo);
        if (selectedCombo) {
          item.points = selectedCombo.combo_points;
          item.comboItems = selectedCombo.items.map(i => `${i.item_name} (${i.item_moq} ${i.item_baseUnit})`);
          item.customized = false;
        }
      }
    },

    openCustomizationDialog(item) {
      this.currentCustomizationIndex = this.mealPlan.indexOf(item);
      this.customMeal = {
        flatbread: '',
        flatbreadQuantity: 0,
        rice: '',
        riceQuantity: 0,
        curry: '',
        curryPortion: '',
        dry: '',
        dryPortion: '',
        dalCurry: false,
        salad: false,
        curd: false,
        dessert: false,
        points: 0,
        extraItems: []
      };
      this.customizationDialog = true;
    },

    closeCustomizationDialog() {
      this.customizationDialog = false;
      this.currentCustomizationIndex = null;
    },

    saveCustomization() {
      if (this.currentCustomizationIndex !== null) {
        const item = this.mealPlan[this.currentCustomizationIndex];
        item.points = this.customMeal.points;
        item.customized = true;
        item.mealCombo = 'custom';
        item.comboItems = this.getCustomComboItems();
      }
      this.closeCustomizationDialog();
    },

    getCustomComboItems() {
      const items = [];
      if (this.customMeal.flatbread) {
        const flatbread = this.getFlatbreadOptions().find(i => i.id === this.customMeal.flatbread);
        items.push(`${flatbread.item_name} (${this.customMeal.flatbreadQuantity})`);
      }
      if (this.customMeal.rice) {
        const rice = this.getRiceOptions().find(i => i.id === this.customMeal.rice);
        items.push(`${rice.item_name} (${this.customMeal.riceQuantity}g)`);
      }
      if (this.customMeal.curry) {
        const curry = this.getCurryOptions().find(i => i.id === this.customMeal.curry);
        items.push(`${curry.item_name} (${this.customMeal.curryPortion})`);
      }
      if (this.customMeal.dry) {
        const dry = this.getDryOptions().find(i => i.id === this.customMeal.dry);
        items.push(`${dry.item_name} (${this.customMeal.dryPortion})`);
      }
      if (this.customMeal.dalCurry) items.push('Dal Curry');
      if (this.customMeal.salad) items.push('Salad');
      if (this.customMeal.curd) items.push('Curd');
      if (this.customMeal.dessert) items.push('Dessert');
      this.customMeal.extraItems.forEach(item => items.push(item.name));
      return items;
    },

    calculateCustomMealPoints() {
      let points = 0;
      
      const flatbread = this.getFlatbreadOptions().find(i => i.id === this.customMeal.flatbread);
      if (flatbread) {
        points += this.customMeal.flatbreadQuantity * flatbread.item_point;
      }
      
      const rice = this.getRiceOptions().find(i => i.id === this.customMeal.rice);
      if (rice) {
        points += Math.ceil(this.customMeal.riceQuantity / rice.item_baseUnit) * rice.item_point;
      }
      
      const curry = this.getCurryOptions().find(i => i.id === this.customMeal.curry);
      if (curry) {
        const portionMultiplier = this.customMeal.curryPortion === 'Small' ? 0.5 : this.customMeal.curryPortion === 'Large' ? 1.5 : 1;
        points += curry.item_point * portionMultiplier;
      }
      
      const dry = this.getDryOptions().find(i => i.id === this.customMeal.dry);
      if (dry) {
        const portionMultiplier = this.customMeal.dryPortion === 'Small' ? 0.5 : this.customMeal.dryPortion === 'Large' ? 1.5 : 1;
        points += dry.item_point * portionMultiplier;
      }
      
      if (this.customMeal.dalCurry) points += 1;
      if (this.customMeal.salad) points += 1;
      if (this.customMeal.curd) points += 1;
      if (this.customMeal.dessert) points += 1;
      
      if (this.customMeal.extraItems) {
        this.customMeal.extraItems.forEach(item => {
          points += item.points;
        });
      }
      
      this.customMeal.points = Math.round(points);
    },

    saveMealSelection(item) {
      console.log('Saving meal selection:', item);
      item.saved = true;
      this.snackbarText = `Meal saved for ${this.formatDate(item.date)}`;
      this.snackbarColor = 'success';
      this.snackbar = true;
      this.updateAvailablePoints();
    },

    updateAvailablePoints() {
      const usedPoints = this.mealPlan.reduce((total, meal) => total + (meal.saved ? meal.points : 0), 0);
      this.availablePoints = 70 - usedPoints; // 70 is the total points for a week (10 per day)
    },

    getChipColor(comboItem) {
      const item = this.mealCombos.flatMap(combo => combo.items).find(i => i.item_name === comboItem.split(' (')[0]);
      if (item) {
        if (item.item_type === 'vegetarian') return 'green';
        if (item.item_type === 'egg') return 'orange';
        if (['non-vegetarian', 'chicken', 'fish', 'mutton'].includes(item.item_type)) return 'red';
      }
      return 'default';
    },

    canEditOrSave(date) {
      const today = new Date();
      const selectedDate = new Date(date);
      
      // Set time to start of day for consistent comparisons
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      // Calculate the Friday of the current week
      const fridayOfCurrentWeek = new Date(today);
      fridayOfCurrentWeek.setDate(today.getDate() + ((5 + 7 - today.getDay()) % 7));

      // Check if the selected date is in the current week
      if (selectedDate <= fridayOfCurrentWeek) {
        return selectedDate >= today; // Can edit/save only for today and future dates in the current week
      }

      // For future weeks, check if today is before or on Friday of the current week
      return today <= fridayOfCurrentWeek;
    },

    canCancelMeal(date) {
      const mealDate = new Date(date);
      const today = new Date();
      const fridayBeforeMeal = new Date(mealDate);
      fridayBeforeMeal.setDate(mealDate.getDate() - ((mealDate.getDay() + 2) % 7));
      
      return today <= fridayBeforeMeal;
    },

    cancelMeal(item) {
    //   const today = new Date();
      const mealDate = new Date(item.date);
      let refundPoints = 0;

      if (this.isCurrentWeek(mealDate)) {
        refundPoints = Math.floor(item.points / 2); // Half points refund for current week
      } else {
        refundPoints = item.points; // Full points refund for future weeks
      }

      this.availablePoints += refundPoints;
      item.mealCombo = '';
      item.comboItems = [];
      item.points = 0;
      item.saved = false;

      this.snackbarText = `Meal cancelled for ${this.formatDate(item.date)}. ${refundPoints} points refunded.`;
      this.snackbarColor = 'info';
      this.snackbar = true;
    },

    isCurrentWeek(date) {
      const today = new Date();
      const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay() + 1));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      return date >= startOfWeek && date <= endOfWeek;
    },

    isPastMeal(date) {
      const mealDate = new Date(date);
      const today = new Date();
      mealDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return mealDate < today;
    },

    onMealTypeChange() {
      // Reset meal combos for all days when meal type changes
      this.mealPlan.forEach(day => {
        day.mealCombo = '';
        day.comboItems = [];
        day.points = 0;
        day.customized = false;
      });
    },

    addExtraItem() {
      if (!this.customMeal.extraItems) {
        this.$set(this.customMeal, 'extraItems', []);
      }
      this.customMeal.extraItems.push({ name: '', points: 0 });
    },

    removeExtraItem(index) {
      this.customMeal.extraItems.splice(index, 1);
      this.calculateCustomMealPoints();
    },

    getFlatbreadOptions() {
      return this.mealCombos.flatMap(combo => combo.items).filter(item => item.item_type === 'flatbread');
    },

    getRiceOptions() {
      return this.mealCombos.flatMap(combo => combo.items).filter(item => item.item_type === 'rice');
    },

    getCurryOptions() {
      return this.mealCombos.flatMap(combo => combo.items).filter(item => item.item_type.includes('curry'));
    },

    getDryOptions() {
      return this.mealCombos.flatMap(combo => combo.items).filter(item => item.item_type.includes('dry'));
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  }
};
</script>

<style scoped>
.v-data-table {
  margin-top: 20px;
}

.v-chip-group {
  max-width: 100%;
  flex-wrap: wrap;
}

.v-chip {
  margin-bottom: 5px;
}
</style>